import React from 'react'

import { Col, Icon, Row, Tooltip } from 'antd'
import get from 'lodash/get'
import maxBy from 'lodash/maxBy'
import PT from 'prop-types'

import colors from '../../components/hocs/colors'
import numberConverter from '../../utils/numberConverter'
import { capitalize } from '../../utils/stringManipulation'

export const SimpleBar = ({
  ariaDescribedBy,
  ariaLabelledBy,
  barColor,
  data = [],
  dataTestId,
  disabled,
  isSalary,
  maxHeight = 300,
  onBarClick,
  selectedValue,
}) => {
  const max = maxBy(data, 'doc_count')

  let maxSalary = 0
  try {
    const maxSalaryItem = maxBy(data, 'metric')
    maxSalary = get(maxSalaryItem, 'metric', 0)
  } catch (error) {
    console.log(error)
    maxSalary = 0
  }

  let computedBarcolor = isSalary ? colors.blue1 : colors.blue2
  computedBarcolor = barColor ? barColor : computedBarcolor

  const handleKeyDown = (e, childKey) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled && onBarClick) {
      e.preventDefault()
      onBarClick(childKey)
    }
  }

  return (
    <ul
      style={{
        listStyle: 'none',
        margin: '0 auto',
        maxHeight: maxHeight,
        maxWidth: 300,
        overflow: 'auto',
        paddingBottom: 20,
      }}
      className="scrolling-container"
      data-testid={dataTestId}
      aria-label={
        ariaLabelledBy ? undefined : disabled ? 'Bar chart data' : 'Filterable bar chart data'
      }
      aria-describedby={ariaDescribedBy}
      aria-labelledby={ariaLabelledBy}
    >
      {data.map((child, index) => {
        const childKey = get(child, 'key', '')
        const pct = max
          ? Math.round((get(child, 'doc_count', 0) * 100) / get(max, 'doc_count', 1))
          : 0

        const barContent = (
          <Tooltip
            placement="topLeft"
            title={
              !!get(child, 'metric', 0) &&
              `Avg. Salary: $${numberConverter(get(child, 'metric', 0))}`
            }
          >
            <span
              style={{
                clip: 'rect(0,0,0,0)',
                height: 1,
                overflow: 'hidden',
                position: 'absolute',
                whiteSpace: 'nowrap',
                width: 1,
              }}
            >
              {`${capitalize(childKey)}, ${pct}% of results`}
            </span>
            <div
              style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}
              aria-hidden="true"
            >
              {capitalize(childKey)}
            </div>
            {maxSalary ? (
              <div>
                <div>
                  <div className="scale-bars-icon">
                    <Icon type="number" aria-hidden="true" />
                  </div>
                  <div className="scale-bars-right">
                    <div
                      style={{
                        background:
                          selectedValue === childKey
                            ? computedBarcolor
                            : selectedValue
                            ? colors.simpleBarNotClickedBackground
                            : computedBarcolor,
                        border: `1px solid ${computedBarcolor}`,
                        marginTop: 2,
                        width: `${(get(child, 'doc_count', 0) * 100) / get(max, 'doc_count', 0)}%`,
                      }}
                      className="scale-bars"
                    />
                  </div>
                </div>
                {!!get(child, 'metric') && (
                  <div style={{ marginTop: 5 }}>
                    <div className="scale-bars-icon">
                      <Icon type="dollar" aria-hidden="true" />
                    </div>
                    <div className="scale-bars-right">
                      <div
                        style={{
                          background:
                            selectedValue === childKey
                              ? colors.salary
                              : selectedValue
                              ? colors.simpleBarNotClickedBackground
                              : colors.salary,
                          border: `1px solid ${colors.salary}`,
                          marginTop: 2,
                          width: `${(get(child, 'metric', 0) * 100) / maxSalary}%`,
                        }}
                        className="scale-bars"
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div
                style={{
                  background:
                    selectedValue === childKey
                      ? computedBarcolor
                      : selectedValue
                      ? colors.simpleBarNotClickedBackground
                      : computedBarcolor,
                  border: `1px solid ${computedBarcolor}`,
                  width: `${(get(child, 'doc_count', 0) * 100) / get(max, 'doc_count', 0)}%`,
                }}
                className="scale-bars"
              />
            )}
          </Tooltip>
        )

        return (
          <li key={`${JSON.stringify(child)}${index}`}>
            <Row type="flex" align="middle" style={{ marginBottom: '15px ' }}>
              <Col span={22}>
                {disabled ? (
                  <div style={{ borderRadius: 3, cursor: 'default', marginLeft: '8px' }}>
                    {barContent}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => onBarClick(childKey)}
                    onKeyDown={e => handleKeyDown(e, childKey)}
                    aria-label={`Filter by ${capitalize(childKey)}, ${pct}% of results`}
                    aria-describedby={ariaDescribedBy}
                    aria-pressed={selectedValue === childKey}
                    style={{
                      background: 'none',
                      border: 'none',
                      borderRadius: 3,
                      cursor: 'pointer',
                      marginLeft: '8px',
                      padding: 0,
                      textAlign: 'left',
                      width: '100%',
                    }}
                  >
                    {barContent}
                  </button>
                )}
              </Col>
            </Row>
          </li>
        )
      })}
    </ul>
  )
}

SimpleBar.propTypes = {
  ariaDescribedBy: PT.string,
  ariaLabelledBy: PT.string,
  barColor: PT.string,
  data: PT.array,
  dataTestId: PT.string,
  disabled: PT.bool,
  isSalary: PT.bool,
  maxHeight: PT.number,
  onBarClick: PT.func,
  selectedValue: PT.string,
}

export default SimpleBar
